import React, { forwardRef, HTMLAttributes, PropsWithChildren } from 'react'

import type { IconType } from '@helpscout/hsds-icons/baseIcon'
import Search from '@helpscout/hsds-icons/search-large'
import classNames from 'classnames'

import {
  COLOR_BLUE,
  COLOR_GREEN,
  COLOR_GREY,
  SIZE_LG,
  SIZE_MD,
  SIZE_SM,
  SIZE_XL,
  SIZE_XS,
  useButtonAs,
  useButtonClassnames,
} from 'hsds/components/button'
import type { ButtonProps } from 'hsds/components/button/Button.types'
import Icon from 'hsds/components/icon'
import { mapStatusToColor } from 'hsds/components/status-dot/StatusDot.styles'
import { getValidProps } from 'hsds/utils/react'

import { AttentionUI, ButtonIconUI, SuffixIconUI } from './ButtonIcon.styles'

export const COLOR_PURPLE = 'purple'
export const COLOR_WHITE = 'white'
export const COLOR_CHARCOAL = 'charcoal'
export const COLOR_CLAY = 'clay'

export const SIZES = [SIZE_XL, SIZE_LG, SIZE_MD, SIZE_SM, SIZE_XS] as const
export const COLORS = [
  COLOR_GREEN,
  COLOR_GREY,
  COLOR_PURPLE,
  COLOR_WHITE,
  COLOR_BLUE,
  COLOR_CHARCOAL,
  COLOR_CLAY,
] as const

type SizeType = (typeof SIZES)[number]
export type ColorType = (typeof COLORS)[number]
export type ButtonIconProps = {
  /** Change the html element used for the component. */
  as?: string
  /** Custom class names to be added to the component. */
  className?: string
  /** Applies a color based style to the button. */
  color?: ColorType
  /** Disable the button so it can't be clicked. */
  disabled?: boolean
  /** Remove button background and border. Same as color="white" */
  seamless?: boolean
  filled?: boolean
  /** Display an attention indicator */
  attention?:
    | true
    | false
    | 'default'
    | 'active'
    | 'inactive'
    | 'offline'
    | 'busy'
    | 'custom'
  /** The icon to render. */
  icon?: IconType
  /** Data attr for Cypress tests. */
  'data-cy'?: string
  /** Sets the size of the button. */
  size?: SizeType
  /** onclick handler */
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement>
  ) => void | boolean | Promise<void | boolean | undefined>
  /** React Router path to navigate on click. */
  to?: string
  href?: string
  target?: string
  submit?: boolean
  suffix?: React.ReactNode
} & HTMLAttributes<HTMLButtonElement>

const useButtonIcon = (props: PropsWithChildren<ButtonIconProps>) => {
  const {
    children,
    icon,
    seamless,
    size,
    title,
    attention,
    color: colorProp,
    suffix,
    ...rest
  } = props

  const color = seamless ? COLOR_WHITE : colorProp

  const forcedProps = {
    // ButtonIcon is always rounded, makes it easier to overwrite properties without the classname
    rounded: true,
    size: SIZES.includes(size as SizeType) ? size : 'xl',
    filled: true,
    color: COLORS.includes(color as ColorType) ? color : COLOR_WHITE,
    seamless,
  }

  const additionalProps = useButtonAs(props as ButtonProps)

  const buttonProps = {
    ...rest,
    ...forcedProps,
    ...additionalProps,
  }

  const ariaLabel = title || rest['aria-label'] || undefined

  // remove color from props
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { color: _color, ...restButtonProps } = buttonProps

  return {
    'data-testid': 'IconButton',
    ...getValidProps(restButtonProps),
    className: useButtonClassnames(
      'c-ButtonIcon c-IconButton',
      buttonProps as ButtonProps
    ),
    'aria-label': ariaLabel,
    icon,
    children,
    attention,
    suffix,
  }
}

const WrappedButtonIcon = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonIconProps>
>(function ButtonIcon(props, ref) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children: _children,
    icon,
    attention,
    suffix,
    ...buttonProps
  } = useButtonIcon(props)

  return (
    <ButtonIconUI {...buttonProps} ref={ref}>
      <Icon icon={icon} size={24} title={buttonProps['aria-label']} />
      {attention && (
        <AttentionUI
          data-testid="IconButton.Attention"
          className={classNames(
            'c-IconButton__Attention', // backwards compat
            'c-ButtonIcon__Attention',
            typeof attention === 'boolean' &&
              attention &&
              `is-color-${mapStatusToColor('offline')}`,
            typeof attention === 'string' &&
              `is-color-${mapStatusToColor(attention)}`
          )}
        />
      )}
      {suffix && (
        <SuffixIconUI className="c-IconButton__Suffix">{suffix}</SuffixIconUI>
      )}
    </ButtonIconUI>
  )
})

WrappedButtonIcon.defaultProps = {
  disabled: false,
  color: 'grey',
  'data-cy': 'IconButton',
  icon: Search,
  attention: false,
}

WrappedButtonIcon.displayName = 'IconButton'

export default WrappedButtonIcon
